import { useQueryClient } from "@tanstack/react-query";
import {
  Bot,
  Building2,
  FileText,
  FileUp,
  HeartPulse,
  History,
  Inbox,
  LayoutDashboard,
  LogOut,
  type LucideIcon,
  MessagesSquare,
  UserPlus,
  Users,
} from "lucide-react";
import { NavLink, Outlet, useNavigate } from "react-router";
import { api } from "../api/client";
import { NotificationBell, type NotificationList } from "../components/NotificationBell";
import { Alert, ICON, Logo } from "../components/ui";
import { get, post, useAdmin } from "./ui";

// Back-office layout: left navigation grouped by job (business / operations / accounts), the signed-in admin and
// sign-out on top; on phones the navigation becomes a horizontal strip under the top bar.

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  badge?: "support" | "fiscal" | "chat";
}

const NAV: { label: string; items: NavItem[] }[] = [
  {
    label: "Afacere",
    items: [
      { to: "/admin", label: "Panou", icon: LayoutDashboard, end: true },
      { to: "/admin/restaurante", label: "Restaurante", icon: Building2 },
      { to: "/admin/abonamente", label: "Abonamente și facturi", icon: FileText, badge: "fiscal" },
    ],
  },
  {
    label: "Operare",
    items: [
      { to: "/admin/importuri", label: "Importuri", icon: FileUp },
      { to: "/admin/ai", label: "AI și costuri", icon: Bot },
      { to: "/admin/sistem", label: "Sănătate sistem", icon: HeartPulse },
    ],
  },
  {
    label: "Conturi și suport",
    items: [
      { to: "/admin/chat", label: "Chat clienți", icon: MessagesSquare, badge: "chat" },
      { to: "/admin/suport", label: "Mesaje site", icon: Inbox, badge: "support" },
      { to: "/admin/utilizatori", label: "Utilizatori", icon: Users },
      { to: "/admin/invitatii", label: "Invitații", icon: UserPlus },
      { to: "/admin/audit", label: "Jurnal (audit)", icon: History },
    ],
  },
];

interface Badges {
  attention: { supportOpen: number; fiscalFailed: number; chatUnread: number };
}

export function AdminShell({ email, mfaOff = false }: { email: string; mfaOff?: boolean }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const biz = useAdmin<Badges>(["business"], "/business");
  const badge = (k?: NavItem["badge"]) =>
    k === "support"
      ? biz.data?.attention.supportOpen
      : k === "fiscal"
        ? biz.data?.attention.fiscalFailed
        : k === "chat"
          ? biz.data?.attention.chatUnread
          : 0;
  async function logout() {
    await api("POST", "/auth/logout").catch(() => undefined);
    qc.clear();
    navigate("/admin/login", { replace: true });
  }
  return (
    <div className="shell">
      <aside className="sidebar" aria-label="Navigare administrare">
        <div className="row" style={{ gap: 8, alignItems: "center" }}>
          <Logo className="sidebar-logo" white />
          <span className="chip chip-info">Admin</span>
        </div>
        <nav className="stack" style={{ gap: 20 }}>
          {NAV.map((g) => (
            <div key={g.label}>
              <h2 className="nav-group-label">{g.label}</h2>
              <ul className="nav-list">
                {g.items.map((i) => {
                  const n = badge(i.badge);
                  return (
                    <li key={i.to}>
                      <NavLink to={i.to} end={i.end} className="nav-item">
                        <i.icon {...ICON} aria-hidden="true" />
                        <span style={{ flex: 1 }}>{i.label}</span>
                        {!!n && <span className="admin-badge">{n}</span>}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>
      <div className="main-col">
        <header className="topbar">
          <img
            className="mobile-only"
            src="/brand/meniova-symbol.svg"
            alt="Meniova"
            style={{ height: 22, width: "auto" }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p className="topbar-title">Meniova · Administrare</p>
            <div className="topbar-sub">{email}</div>
          </div>
          <NotificationBell
            scope="admin"
            load={() => get<NotificationList>("/notifications")}
            markRead={() => post<void>("/notifications/read")}
          />
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => void logout()}>
            <LogOut {...ICON} aria-hidden="true" />
            Ieșire
          </button>
        </header>
        <nav className="admin-strip" aria-label="Secțiuni administrare">
          {NAV.flatMap((g) => g.items).map((i) => (
            <NavLink key={i.to} to={i.to} end={i.end}>
              {i.label}
            </NavLink>
          ))}
        </nav>
        <main className="main">
          <div className="main-inner admin-main">
            {mfaOff && (
              <Alert tone="warning" title="Autentificarea în doi pași nu este activă pe server.">
                Lipsește secretul TOTP_KEY. După ce îl setezi, la următoarea intrare ți se cere configurarea
                codului.
              </Alert>
            )}
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
